/**
 * petFilters.js — Pet-based collection & search filtering.
 * Loaded with defer AFTER facets.js so FacetFiltersForm is available.
 *
 * Responsibilities:
 *  - Consume the early prefetch started by pet-filter-init.liquid (collection pages)
 *  - Trigger Luigi search with pet filters already in the URL (search pages)
 *  - Expose window.runPetFiltersLogic(pet) for the pet selector extension
 *  - Detect manual filter changes and deselect pet when filters no longer match
 */
(function () {
  // ── Constants ──────────────────────────────────────────────────────────────

  const ANIMAL_TYPES = { DOG: 'Dog', CAT: 'Cat' };

  /** Czech filter values for each animal type */
  const ANIMAL_TYPE_FILTER_VALUES = { Dog: 'Pes', Cat: 'Kočka' };

  /** Pseudo-pet used when no specific pet is selected */
  const ALL_PETS_ITEM = { id: 'all', name: 'all', animal_type: 'all', avatar_url: null };

  // ── Selectors ──────────────────────────────────────────────────────────────

  const FACET_CHECKBOXES =
    '#FacetFiltersForm input[type="checkbox"], ' +
    '#FacetFiltersForm-bar input[type="checkbox"], ' +
    '.facets-desktop-container input[type="checkbox"], ' +
    '.facets input[type="checkbox"]';

  const FACET_REMOVE =
    '#facet-remove, .facet-remove, .facets__item-remove, ' +
    '[class*="facet-remove"], [class*="facets__item-remove"], ' +
    '[id*="facet-remove"], a[href*="filter."]';

  // ── Analytics ──────────────────────────────────────────────────────────────

  function phCapture(event, props) {
    try { window.posthog?.capture(event, props); } catch (_) { }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** True on collection and search pages; false on product pages. */
  function isApplicablePage() {
    const p = location.pathname || '';
    return (p.includes('/collections/') && !p.includes('/products/')) || p.includes('/search');
  }

  /** Filter value → Shopify param name map set by the inline Liquid snippet. */
  function getFilterMap() {
    return window.__petFilterMap || {};
  }

  /** Read selectedPet from localStorage (null-safe). */
  function getSelectedPet() {
    try {
      return JSON.parse(localStorage.getItem('selectedPet') || 'null');
    } catch {
      return null;
    }
  }

  // ── Filter param building ──────────────────────────────────────────────────

  /** Append a filter param only if the value exists in the current collection/search. */
  function appendFilterIfExists(params, value) {
    if (!value) return;
    const paramName = getFilterMap()[value];
    if (paramName) params.append(paramName, value);
  }

  /**
   * Build URLSearchParams with all pet-relevant filters.
   * Values that don't exist in the current collection/search are silently skipped.
   */
  function buildFilterParams(pet) {
    const params = new URLSearchParams();
    if (!pet || !pet.id || pet.id === 'all') return params;

    const { animal_type, dog_size_metafield_value_cz, breed_type_cat_metafield_value_cz, lifestage, properties } = pet;

    // Animal type
    const animalValue = ANIMAL_TYPE_FILTER_VALUES[animal_type];
    if (animalValue) appendFilterIfExists(params, animalValue);

    // Breed size / breed type
    if (animal_type === ANIMAL_TYPES.DOG && dog_size_metafield_value_cz) {
      appendFilterIfExists(params, dog_size_metafield_value_cz);
    } else if (animal_type === ANIMAL_TYPES.CAT && breed_type_cat_metafield_value_cz) {
      appendFilterIfExists(params, breed_type_cat_metafield_value_cz);
    }

    // Life stage
    if (lifestage) appendFilterIfExists(params, lifestage);

    // Allergies & health conditions
    if (properties) {
      (properties.allergies || []).forEach((a) => {
        if (a.special_type_of_feed_metafield_value_cz) appendFilterIfExists(params, a.special_type_of_feed_metafield_value_cz);
      });
      (properties.health_conditions || []).forEach((c) => {
        if (c.health_feature_metafield_value_cz) appendFilterIfExists(params, c.health_feature_metafield_value_cz);
      });
    }

    return params;
  }

  // ── Filter match checking ──────────────────────────────────────────────────
  // When the user manually changes filters (unchecks a checkbox, removes a pill),
  // the URL filters may no longer match the pet. In that case we switch to "all pets"
  // so the pet selector UI stays in sync.

  /** Sorted, deduplicated list of filter.* param keys. */
  function getFilterKeys(params) {
    return [...new Set([...params.keys()].filter((k) => k.startsWith('filter.')))].sort();
  }

  /** True when the URL's filter params exactly match the pet's expected filters. */
  function filtersMatchUrl(petParams, currentSearch) {
    const urlParams = new URLSearchParams(currentSearch);
    const urlKeys = getFilterKeys(urlParams);
    const expectedKeys = getFilterKeys(petParams);

    if (urlKeys.length !== expectedKeys.length || !urlKeys.every((k, i) => k === expectedKeys[i])) return false;

    return urlKeys.every((k) => {
      const a = urlParams.getAll(k).sort();
      const b = petParams.getAll(k).sort();
      return a.length === b.length && a.every((v, i) => v === b[i]);
    });
  }

  /** Reset to "all pets" and notify the pet selector extension. */
  function switchToAllPets() {
    const oldPet = getSelectedPet();
    phCapture('shopify_pet_deselected_by_filter', {
      old_pet_id: oldPet?.id,
      old_pet_name: oldPet?.name,
      remaining_filters: [...new URLSearchParams(location.search).entries()]
        .filter(([k]) => k.startsWith('filter.'))
        .map(([k, v]) => `${k}=${v}`),
    });
    localStorage.setItem('selectedPet', JSON.stringify(ALL_PETS_ITEM));
    window.dispatchEvent(new CustomEvent('localStorageChange', { detail: { key: 'selectedPet', value: ALL_PETS_ITEM } }));

    const params = new URLSearchParams(window.location.search);
    if (params.has('selectedPet')) {
      params.delete('selectedPet');
      history.replaceState({ searchParams: params.toString() }, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''));
    }
  }

  /** If the current URL filters don't match the pet, switch to "all". */
  function runFiltersMatchChecker() {
    const pet = getSelectedPet();
    if (!pet || !pet.id || pet.id === 'all') return;
    if (!filtersMatchUrl(buildFilterParams(pet), location.search)) switchToAllPets();
  }

  // ── Public API ─────────────────────────────────────────────────────────────
  // Called by the pet selector extension (petSelector.js) when the user picks a pet.
  // [TEMPORARILY DISABLED] — Collection page prefetch consumer: was consuming the __petFilterFetch

  // function runPetFiltersLogic(pet) {
  //   if (!isApplicablePage()) return;

  //   const currentParams = new URLSearchParams(window.location.search);

  //   // Strip old filter.* and selectedPet, then apply new pet filters
  //   [...currentParams.keys()]
  //     .filter((k) => k.startsWith('filter.') || k === 'selectedPet')
  //     .forEach((k) => currentParams.delete(k));

  //   const petParams = buildFilterParams(pet);
  //   for (const [k, v] of petParams) currentParams.append(k, v);

  //   if (pet && pet.id && pet.id !== 'all') {
  //     currentParams.set('selectedPet', pet.id);
  //     phCapture('shopify_filter_applied', {
  //       source: 'pet_selection',
  //       pet_id: pet.id,
  //       pet_name: pet.name,
  //       animal_type: pet.animal_type,
  //       filters: [...petParams.entries()].map(([k, v]) => `${k}=${v}`),
  //       page: location.pathname,
  //     });
  //   }

  //   // Delegate to facets.js which handles fetch, DOM replacement, and Luigi search
  //   if (typeof FacetFiltersForm !== 'undefined') {
  //     FacetFiltersForm.renderPage(currentParams.toString());
  //   } else {
  //     const qs = currentParams.toString();
  //     location.href = qs ? `${location.pathname}?${qs}` : location.pathname;
  //   }
  // }

  // window.runPetFiltersLogic = runPetFiltersLogic;

  // ── Initialization ─────────────────────────────────────────────────────────

  // [TEMPORARILY DISABLED] — Pet filter application: FacetFiltersForm.applySection is skipped so
  // pet filters are not applied on collection page load. The promise is still consumed and
  // pet-filters-pending is still removed so the product grid remains visible.
  if (window.__petFilterFetch) {
    window.__petFilterFetch
      .then(function () {
        document.documentElement.classList.remove('pet-filters-pending');
      })
      .catch(function () {
        document.documentElement.classList.remove('pet-filters-pending');
      });
  }

  // [TEMPORARILY DISABLED] — Luigi search page init: was triggering FacetFiltersForm.renderPage
  // on search pages to sync facet checkboxes and kick off Luigi search with pet filters already
  // in the URL (pre-set by the inline Liquid snippet).
  // if (!window.__petFilterFetch && document.querySelector('luigi-search-page')) {
  //   const pet = getSelectedPet();
  //   if (pet && pet.id && pet.id !== 'all') {
  //     if (typeof FacetFiltersForm !== 'undefined') {
  //       FacetFiltersForm.renderPage(window.location.search.slice(1), null, false);
  //     } else {
  //       document.dispatchEvent(new CustomEvent('facets:luigi-search-refresh'));
  //     }
  //   }
  // }

  // ── Event listeners ────────────────────────────────────────────────────────

  function setupListeners() {
    // Manual checkbox change → check if filters still match pet
    document.addEventListener('change', function (e) {
      if (e.target.type === 'checkbox' && e.target.matches && e.target.matches(FACET_CHECKBOXES)) {
        setTimeout(runFiltersMatchChecker, 600);
      }
    });

    // Filter pill removal → same check
    document.addEventListener('click', function (e) {
      if (e.target.closest(FACET_REMOVE)) {
        setTimeout(runFiltersMatchChecker, 600);
      }
    });

    // Back/forward navigation
    window.addEventListener('popstate', function () {
      if (isApplicablePage()) setTimeout(runFiltersMatchChecker, 300);
    });

    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.mobile-filters-clear, .clear-filters-btn');
      if (!btn) return;
      const pet = getSelectedPet();
      if (pet && pet.id && pet.id !== 'all') {
        switchToAllPets();
      }
    });
  }

  // [TEMPORARILY DISABLED] — Event listener setup: was attaching checkbox/click/popstate listeners
  // that auto-deselect the active pet when the user manually changes filters so the pet selector
  // UI stays in sync.
  // if (isApplicablePage()) setupListeners();
})();
